<script setup lang="ts">
import type { UpdateEventListDetail } from '../types';
import { invokeGet, removeAccents } from '../js-utils/_shared/form-utils';
import { onMounted, ref, type ComponentInternalInstance } from 'vue';

// declare required types / interfaces
interface ComboboxItem {
    id: number;
    nombre: string;
}

// declare the component props
const props = defineProps<{
    _rootElement: HTMLElement; // reference to the component root element, passed from the parent component
    rootClass: string; // additional class to add to the root element, passed from the parent component
    selectorLabel: string;
    selectorPlaceholder: string;
    searchLabel: string;
    headerIcon: string;
    infoMsg: string; // informational message to show below the combobox
    apiOnChange: string; // API endpoint to call when an option is selected
    items: ComboboxItem[];
    isMultiple: boolean;
    valueName: string; // name of the hidden input that will store the selected value(s)
    storeTextAsValue: boolean; // whether to store the option text instead of its ID
    childUpdateOnChange: string; // name of the custom event to dispatch when an option is selected, to update child components
    updateEvent: string; // name of the event to listen for updating this component options
    selectedValue: string; // preexisting value of the combobox selection (non multiple)
    selectedText: string; // preexisting text of the combobox selection (non multiple)
    hideIfOnlyOne: boolean;
}>();

// declare the reactive properties
const combobox = ref<HTMLElement | null>(null);
const label = ref<HTMLElement | null>(null);
const header = ref<HTMLElement | null>(null);
const optionsContainer = ref<HTMLElement | null>(null);
const comboboxContent = ref<HTMLElement | null>(null);
const options = ref<HTMLElement[]>([]);
const arrow = ref<HTMLElement | null>(null);
const searchInput = ref<HTMLInputElement | null>(null);
const searchItem = ref<HTMLInputElement | null>(null);
const hiddenValue = ref<HTMLInputElement | null>(null);
const hiddenText = ref<HTMLElement | null>(null);
const infoMsg = ref<HTMLElement | null>(null);
const items = ref<ComboboxItem[]>(props.items);
const showCombobox = ref<boolean>(false);

// declare references to external elements

// other properties

/* -------------------------------------------------------------
    Implement tasks on component mount
 */
onMounted(async (callback: () => void, target?: ComponentInternalInstance | null) => {
    // set a class for the root element (v-app: the div that wraps the component),
    // so we keep the flex layout and the full width of the parent container
    props._rootElement.classList.add(props.rootClass);

    // assign the initial value to the header label (selected option or placeholder)
    label.value!.textContent = props.selectedText || props.selectorPlaceholder || '';

    // comboboxContent.value?.addEventListener('click', function (event: Event) {
    //     event.stopPropagation();
    //     if (searchInput.value && optionsContainer.value) {
    //         searchInput.value!.value = '';
    //         options.value.forEach((item: HTMLElement) => {
    //             item.style.display = 'flex';
    //         });
    //     }
    //     updateHeaderText();
    // });

    // close the combobox if clicking outside of it
    document.addEventListener('click', function (event) {
        if (header.value?.classList.contains('js-open') && !combobox.value?.contains((event.target as Node)!)) {
            header.value?.click();
        }
    });

    // listen for the custom event to update the options of the combobox
    if (props.updateEvent) {
        document.addEventListener(props.updateEvent, function (event) {
            // remove all selectable options
            options.value.forEach((item: HTMLElement) => {
                item.remove();
            });
            // reset header and hidden inputs
            label.value!.textContent = '';
            hiddenValue.value!.value = '';
            hiddenText.value!.innerHTML = '';
            if (props.childUpdateOnChange) {
                // clear child options
                document.dispatchEvent(new CustomEvent<UpdateEventListDetail>(
                    props.childUpdateOnChange,
                    { detail: { options: [] } }));
            }

            // receive and process the new set of options
            const customEvent = event as CustomEvent<UpdateEventListDetail>;
            const list: Array<ComboboxItem> = customEvent.detail?.options || [];
            items.value = list;
            prepareOptions(true);
            if (items.value.length > 0 && props.valueName === 'codigo_postal') {
                console.log('Combobox options updated:', items.value);
            }
            if (items.value.length === 1) {
                hiddenText.value!.innerHTML = items.value[0]!.nombre;
                label.value!.textContent = items.value[0]!.nombre;
                hiddenValue.value!.value = props.storeTextAsValue
                    ? items.value[0]!.nombre
                    : items.value[0]!.id.toString();
                showCombobox.value = !props.hideIfOnlyOne;
                updateChild(items.value[0]!.id.toString());
            } else {
                showCombobox.value = items.value.length > 0;
            }
            updateHeaderText(); // Inicializar
        });
    }
    if (props.selectedValue) {
        hiddenValue.value!.value = props.selectedValue;
    }
    if (props.selectedText) {
        hiddenText.value!.innerHTML = props.selectedText;
    }
    if (items.value.length === 1) {
        hiddenText.value!.innerHTML = items.value[0]!.nombre;
        label.value!.textContent = items.value[0]!.nombre;
        hiddenValue.value!.value = props.storeTextAsValue ? items.value[0]!.nombre : items.value[0]!.id.toString();
        showCombobox.value = !props.hideIfOnlyOne;
    } else {
        showCombobox.value = items.value.length > 0;
    }
    prepareOptions(false);
    updateHeaderText(); // Inicializar
});
/* -------------------------------------------------------------
    Event handlers
 */

const comboboxClickHandler = (event: Event) => {
    if (items.value.length > 1) {
        if (props.isMultiple || options.value.length > 1) {
            if (combobox.value?.classList.contains('opened')) {
                combobox.value?.classList.remove('opened');
            } else {
                combobox.value?.classList.add('opened');
                searchInput.value?.focus();
            }
        }
    }
};

const headerClickHandler = function (event: Event) {
    if (items.value.length > 1) {
        comboboxContent.value?.classList.toggle('show');
        header.value?.classList.toggle('js-open');
    }
};

const comboboxContentClickHandler = function (event: Event) {
    event.stopPropagation();
    updateHeaderText();
};

const searchInputInputHandler = function (event: Event) {
    const input = event.currentTarget as HTMLInputElement;
    const searchTerm = removeAccents(input.value.toLowerCase().trim());

    let hasResults = false;

    const labels: NodeListOf<HTMLElement> = optionsContainer.value!.querySelectorAll('label');
    labels.forEach((label) => {
        const parentLi = label.closest('li');
        if (parentLi) {
            if (removeAccents(label.textContent!.toLowerCase()).includes(searchTerm)) {
                parentLi.style.display = 'flex';
                hasResults = true;
            } else {
                parentLi.style.display = 'none';
            }
        }
    });

    // let noResultsElement = optionsContainer.value!.querySelector('.js2-no-results');
    // if (!hasResults) {
    //     if (!noResultsElement) {
    //         noResultsElement = document.createElement('li');
    //         noResultsElement.className = 'combobox-item js2-no-results';
    //         noResultsElement.innerHTML = '<span>No se encontraron resultados</span>';
    //         optionsContainer.value!.appendChild(noResultsElement);
    //     }
    // } else if (noResultsElement) {
    //     noResultsElement.remove();
    // }
};

const optionClickHandler = function (event: Event) {
    const option = event.currentTarget as HTMLElement;
    const combobox = option.closest('.combobox');
    if (combobox && option.textContent) {
        const dropdownHeaderText = combobox.querySelector('.header span');
        combobox?.classList.add('selected');
        if (dropdownHeaderText) {
            dropdownHeaderText.textContent = option.textContent;
            if (!props.isMultiple) {
                const id = option.querySelector('label')?.dataset.id || '';
                hiddenValue.value!.value = props.storeTextAsValue ? option.textContent.trim() : id;
                hiddenText.value!.innerHTML = option.textContent;
                updateChild(id);
            }
            comboboxContent.value!.classList.remove('show');
            header.value!.classList.remove('js-open');
            combobox.classList.remove('opened');
        }
    }
};

const checkboxClickHandler = function (event: Event) {
    const checkbox = event.currentTarget as HTMLInputElement;
    if (checkbox.checked) {
        const parentLi = checkbox.closest('li');
        if (parentLi) {
            if (searchItem.value) {
                optionsContainer.value!.insertBefore(parentLi, searchItem.value.nextSibling);
            } else {
                optionsContainer.value!.insertBefore(parentLi, optionsContainer.value!.firstElementChild);
            }
        }
    }
};

// stop click events from propagating - used to prevent the combobox from opening/closing when clicking
const ignoreClickHandler = (event: Event) => {
    event.stopPropagation();
};

/* -------------------------------------------------------------
    Functions
 */

function updateHeaderText() {
    if (props.isMultiple) {
        const selectedCheckboxes = optionsContainer.value!.querySelectorAll('li label input:checked');

        let labelText = '';
        if (selectedCheckboxes.length === 0) {
            labelText = props.selectorPlaceholder || '';
        } else if (selectedCheckboxes.length <= 2) {
            const selectedLabel = selectedCheckboxes[0]!.parentElement!.textContent!.trim();
            labelText = selectedLabel;
            if (selectedCheckboxes.length === 2) {
                const selectedLabel = selectedCheckboxes[1]!.parentElement!.textContent!.trim();
                labelText += ', ' + selectedLabel;
            }
        } else {
            labelText = 'Selección múltiple';
        }
        const values: string[] = [];
        const texts: string[] = [];
        selectedCheckboxes.forEach((elem) => {
            values.push(elem.parentElement!.dataset.id!);
            texts.push(elem.parentElement!.textContent?.trim() || '');
        });
        if (items.value.length !== 1) {
            label.value!.textContent = labelText;
            hiddenValue.value!.value = props.storeTextAsValue ? texts.join(',') : values.join(',');
            hiddenText.value!.innerHTML = texts.join(',');
        }
        // Vaciar campo de búsqueda
        searchInput.value!.value = '';

        // Mostrar todos los ítems
        options.value.forEach((item: HTMLElement) => {
            item.style.display = 'flex';
        });
    }
}

async function callApiGet(id: string) {
    // special case for localidadesInZona, resolved with a client window object
    if (props.apiOnChange === '(localidadesInZona)') {
        const localidades: Array<string> | undefined = (<any>window).zonasLocals['z' + id.toString()];
        let options: Array<{ id: string; nombre: string }> | null = null;
        if (localidades) {
            options = [];
            localidades.forEach((s) => {
                const segm = s.split('|');
                options!.push({ id: segm[0]!, nombre: segm[1]! });
            });
        }
        return options;
    }

    // call the API with a generic signature
    try {
        const response = await invokeGet(`${props.apiOnChange}?id=${id}`);
        if (response.data) {
            return response.data;
        }
    } catch (error) { }
    return null;
}

function updateChild(id: string) {
    if (props.childUpdateOnChange) {
        const eventName = props.childUpdateOnChange;
        // clear child options
        document.dispatchEvent(new CustomEvent<UpdateEventListDetail>(eventName, { detail: { options: [] } }));
        callApiGet(id).then((data: any) => {
            if (data) {
                // build new child options
                document.dispatchEvent(new CustomEvent<UpdateEventListDetail>(
                    eventName, { detail: { options: data } }));
            }
        });
    }
}


function prepareOptions(forceChildUpdate: boolean) {
    if (!props.isMultiple) {
        if (options.value!.length <= 1) {
            if (options.value!.length == 1) {
                const text = options.value![0]!.textContent?.trim() || '';
                const id = options.value![0]!.querySelector('label')?.dataset.id || '';
                hiddenValue.value!.value = props.storeTextAsValue ? text : id;
                hiddenText.value!.innerHTML = text;
                if (forceChildUpdate) {
                    updateChild(id);
                }
            } else {
                hiddenValue.value!.value = '';
                hiddenText.value!.innerHTML = '';
            }
            label.value!.textContent = hiddenText.value!.innerHTML;
        }
        if (arrow.value) {
            arrow.value!.style.display = options.value!.length <= 1 ? 'none' : 'block';
        }
    }
}
</script>

<template>
    <label v-show="showCombobox" :for="'search-' + props.valueName" :id="'label-' + props.valueName">{{
        props.selectorLabel }}&nbsp;</label>
    <div v-show="showCombobox" ref="combobox" class="combobox" @click="comboboxClickHandler">
        <div ref="header" class="header" @click="headerClickHandler">
            <i :class="props.headerIcon"></i>
            <span ref="label"></span>
            <i v-show="items.length > 1" ref="arrow" class="icon-arrow-down"></i>
        </div>

        <div ref="comboboxContent" class="combobox-content" @click="comboboxContentClickHandler">
            <ul ref="optionsContainer" class="options-container">
                <li ref="searchItem" class="search-item">
                    <span>{{ props.searchLabel }}: </span><input type="text" :id="'search-' + props.valueName"
                        ref="searchInput" class="search-input" @input="searchInputInputHandler" />
                </li>
                <li v-for="item in items" :key="item.id" ref="options" @click="optionClickHandler">
                    <label :data-id="item.id">
                        <input v-if="props.isMultiple" type="checkbox" @click="checkboxClickHandler" />
                        {{ item.nombre }}
                    </label>
                </li>
                <li v-if="!items.length">
                    <span>No se encontraron resultados</span>
                </li>
            </ul>
        </div>
        <input ref="hiddenValue" type="hidden" :name="props.valueName!" />
        <span ref="hiddenText" :class="'js-' + props.valueName + '-texts'" style="display: none"></span>
        <div ref="infoMsg" v-show="items.length > 1" class="info-msg" @click="ignoreClickHandler">
            {{ props.infoMsg }}
        </div>
    </div>
</template>
